'use client';
import React, { useEffect, useRef } from 'react';

interface StatCard {
  value: string;
  label: string;
  sublabel: string;
  color: string;
}

const stats: StatCard[] = [
  {
    value: '4–6x',
    label: 'Perceived Value',
    sublabel: 'vs. flat-print alternatives',
    color: '#ff6b35',
  },
  {
    value: '73%',
    label: 'Brand Recall',
    sublabel: 'With tactile merchandise',
    color: '#00ff88',
  },
  {
    value: '3x',
    label: 'Client Retention',
    sublabel: 'Premium gifting programs',
    color: '#ff6b35',
  },
  {
    value: '100%',
    label: 'Serious Results',
    sublabel: 'Across every product line',
    color: '#00b4ff',
  },
];

interface MarginRow {
  product: string;
  ourCost: string;
  perceivedValue: string;
  margin: string;
  highlight?: boolean;
}

const marginRows: MarginRow[] = [
  {
    product: 'Fleet Cap (3D Leather Patch)',
    ourCost: '$18–$24',
    perceivedValue: '$45–$65',
    margin: '2.5–3x',
    highlight: false,
  },
  {
    product: 'Acrylic Coaster Set (4-pack)',
    ourCost: '$22–$30',
    perceivedValue: '$60–$90',
    margin: '2.7–3x',
    highlight: true,
  },
  {
    product: 'Vintage Motel Keychain',
    ourCost: '$8–$14',
    perceivedValue: '$28–$40',
    margin: '3–3.5x',
    highlight: false,
  },
  {
    product: 'Executive Multi-Tool',
    ourCost: '$28–$38',
    perceivedValue: '$85–$120',
    margin: '3–4x',
    highlight: true,
  },
  {
    product: 'Bamboo Pen Set (3-pack)',
    ourCost: '$16–$22',
    perceivedValue: '$55–$75',
    margin: '3.4x',
    highlight: false,
  },
];

export default function MarginMathSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedEls = sectionRef.current?.querySelectorAll('.animate-in');
    animatedEls?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="margin-math"
      ref={sectionRef}
      className="py-20 px-4 relative overflow-hidden"
      style={{ backgroundColor: 'rgb(3, 8, 16)' }}
    >
      {/* Background accent */}
      <div
        className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00b4ff 0%, transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Copy */}
          <div>
            <p className="section-label">The Margin Math</p>
            <h2
              className="animate-in opacity-0 translate-y-8 transition-all duration-700 font-montserrat font-black text-white mb-6"
              style={{ fontSize: 'clamp(28px, 4vw, 48px)', letterSpacing: '-1px' }}
            >
              Most Businesses Are Leaving{' '}
              <span style={{ color: '#00b4ff' }}>Margin On The Table.</span>
            </h2>
            <p
              className="animate-in opacity-0 translate-y-8 transition-all duration-700 mb-6 leading-relaxed"
              style={{ color: 'rgb(136, 147, 152)', fontSize: '17px', transitionDelay: '100ms' }}
            >
              They&apos;re handing out flat-print shirts and cheap pens. That&apos;s fine. But the
              businesses that will{' '}
              <span className="text-white font-semibold">dominate their market</span> are investing
              in tactile assets that command premium perceived value — and drive repeat business on
              autopilot.
            </p>
            <p
              className="animate-in opacity-0 translate-y-8 transition-all duration-700 mb-8 leading-relaxed"
              style={{ color: 'rgb(136, 147, 152)', fontSize: '17px', transitionDelay: '150ms' }}
            >
              That&apos;s what Serious Swag is built for. Not gimmicks. Not hobby crafts.{' '}
              <span
                className="font-bold"
                style={{ color: '#00b4ff' }}
              >
                Real tactile leverage. Real retention results.
              </span>
            </p>
            <a
              href="#contact"
              className="animate-in opacity-0 translate-y-8 transition-all duration-700 inline-flex items-center gap-2 font-montserrat font-bold uppercase text-white rounded px-8 py-4"
              style={{
                backgroundColor: '#ff6b35',
                border: '2px solid #ff6b35',
                letterSpacing: '0.4px',
                fontSize: '15px',
                transitionDelay: '200ms',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                (e.currentTarget as HTMLElement).style.color = '#ff6b35';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#ff6b35';
                (e.currentTarget as HTMLElement).style.color = '#ffffff';
              }}
            >
              Get Your Unfair Advantage Now
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mt-10">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className="animate-in opacity-0 translate-y-8 transition-all duration-700 rounded-lg p-6 border text-center"
                  style={{
                    backgroundImage: 'linear-gradient(135deg, #071e2e 0%, #0c3a5a 100%)',
                    borderColor: '#1a3a5c',
                    transitionDelay: `${250 + index * 80}ms`,
                  }}
                >
                  <div
                    className="font-montserrat font-black mb-1"
                    style={{ fontSize: '40px', color: stat.color }}
                  >
                    {stat.value}
                  </div>
                  <div className="font-montserrat font-bold text-white text-sm mb-0.5">
                    {stat.label}
                  </div>
                  <div
                    className="text-xs"
                    style={{ color: 'rgb(136, 147, 152)' }}
                  >
                    {stat.sublabel}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Margin Table */}
          <div
            className="animate-in opacity-0 translate-y-8 transition-all duration-700"
            style={{ transitionDelay: '100ms' }}
          >
            <div
              className="rounded-lg border overflow-hidden"
              style={{ borderColor: '#1a3a5c' }}
            >
              {/* Table Header */}
              <div
                className="px-6 py-4 border-b"
                style={{
                  backgroundColor: 'rgba(0, 180, 255, 0.08)',
                  borderColor: '#1a3a5c',
                }}
              >
                <h3
                  className="font-montserrat font-bold text-white text-lg"
                >
                  Product Margin Breakdown
                </h3>
                <p
                  className="text-sm mt-1"
                  style={{ color: 'rgb(136, 147, 152)' }}
                >
                  Retail cost vs. perceived value — the Serious Swag advantage
                </p>
              </div>

              {/* Column Headers */}
              <div
                className="grid grid-cols-4 px-6 py-3 border-b"
                style={{ borderColor: '#1a3a5c', backgroundColor: 'rgba(255,255,255,0.02)' }}
              >
                <div
                  className="font-montserrat font-bold uppercase text-xs col-span-2"
                  style={{ color: 'rgb(136, 147, 152)', letterSpacing: '1px' }}
                >
                  Product
                </div>
                <div
                  className="font-montserrat font-bold uppercase text-xs text-center"
                  style={{ color: 'rgb(136, 147, 152)', letterSpacing: '1px' }}
                >
                  Our Cost
                </div>
                <div
                  className="font-montserrat font-bold uppercase text-xs text-center"
                  style={{ color: '#00b4ff', letterSpacing: '1px' }}
                >
                  Value
                </div>
              </div>

              {/* Rows */}
              {marginRows.map((row, index) => (
                <div
                  key={row.product}
                  className="grid grid-cols-4 px-6 py-4 border-b transition-colors duration-200 hover:bg-white/5"
                  style={{
                    borderColor: '#1a3a5c',
                    backgroundColor: row.highlight ? 'rgba(0, 180, 255, 0.04)' : 'transparent',
                  }}
                >
                  <div
                    className="col-span-2 font-semibold text-sm pr-4"
                    style={{ color: row.highlight ? '#ffffff' : 'rgba(255,255,255,0.8)' }}
                  >
                    {row.product}
                  </div>
                  <div
                    className="text-center text-sm"
                    style={{ color: 'rgb(136, 147, 152)' }}
                  >
                    {row.ourCost}
                  </div>
                  <div
                    className="text-center text-sm font-bold font-montserrat"
                    style={{ color: '#00ff88' }}
                  >
                    {row.margin}
                  </div>
                </div>
              ))}

              {/* Footer note */}
              <div
                className="px-6 py-4"
                style={{ backgroundColor: 'rgba(0, 255, 136, 0.04)' }}
              >
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: 'rgb(136, 147, 152)' }}
                >
                  <span style={{ color: '#00ff88' }}>★</span>{' '}
                  Perceived value multipliers based on industry research and client-reported
                  outcomes. Actual margins vary by order volume and customization level.
                </p>
              </div>
            </div>

            {/* Trust indicators */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[
                { icon: '⚡', text: 'Fast Turnaround', sub: '10–14 business days' },
                { icon: '🔒', text: 'Fixed Pricing', sub: 'No hidden fees' },
                { icon: '🎯', text: 'Minimum Orders', sub: 'Starting at 12 units' },
              ].map((item) => (
                <div
                  key={item.text}
                  className="rounded-lg p-4 text-center border"
                  style={{
                    borderColor: '#1a3a5c',
                    backgroundColor: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <div className="text-xl mb-1">{item.icon}</div>
                  <div className="font-montserrat font-bold text-white text-xs mb-0.5">{item.text}</div>
                  <div className="text-xs" style={{ color: 'rgb(136, 147, 152)' }}>{item.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
